import React, { useCallback, useEffect, useState } from 'react'
import Flex from '@blocks/Flex'
import Grid from '@blocks/Grid'
import { ReactComponent as AddIcon } from '@icons/add.svg'
import { ReactComponent as DeleteIcon } from '@icons/delete.svg'
import Button from '@blocks/Button'
import styled, { css } from 'styled-components'
import { tablet } from '@styles/mixins'
import { colors } from '@styles/vars'

interface EditableListProps<T, O, K> {
    items?: T[],
    options?: O,
    // @ts-ignore
    element: React.ComponentType<T & { idx: number } & { [key in K]: any }>
}

const List = <T extends { id: number }, O extends object, K extends keyof O>({ items, element: Element, options }: EditableListProps<T, O, K>) => {
    const defaultItems = [{ id: 1 }] as T[]
    const [editableItems, setEditableItems] = useState<T[]>((items && items?.length) ? items : defaultItems)

    useEffect(() => {
        if (!items || !items?.length) return
        setEditableItems(items)
    }, [items])

    const addEmptyItem = useCallback(() => {
        setEditableItems(old => {
            const ids = old.map(item => item.id)
            const max = Math.max(...ids)
            const item = { id: max + 1 } as T
            return [...old, item]
        })
    }, [])

    const removeItem = (id: number) => {
        setEditableItems(old => old.filter(item => item.id !== id))
    }

    return (
        <Grid gap="20px">
            <Grid gap="20px">
                {editableItems.map((item, idx) => (
                    <List.Item key={item.id} onDelete={() => removeItem(item.id)} canDelete={editableItems.length > 1}>
                        {/* @ts-ignore */}
                        <Element {...item} {...options} idx={idx} />
                    </List.Item>
                ))}
            </Grid>
            <Flex justifyContent="flex-end">
                <Button type="tertiary" icon={<AddIcon />} onClick={addEmptyItem}>Add</Button>
            </Flex>
        </Grid>
    )
}

interface EditableListItemProps {
    onDelete: () => void,
    canDelete: boolean,
    children: React.ReactNode
}

List.Item = ({ onDelete, canDelete, children }: EditableListItemProps) => {
    return (
        <ItemWrapper>
            {children}
            {canDelete && <Delete onClick={onDelete}><DeleteIcon /><span>Delete</span></Delete>} 
        </ItemWrapper>
    )
}

export default List

/* STYLES */
const ItemWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;

    ${tablet(css`
        flex-direction: row;
    `)}
`

const Delete = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    height: 30px;
    border-radius: 15px;
    transition: .2s ease-out;
    cursor: pointer;
    font-weight: 600;
    margin-top: 10px;

    span {
        margin-left: 5px;
    }

    svg {
        fill: ${colors.darkGrey};
    }

    ${tablet(css`
        width: 30px;
        z-index: 1;
        margin-left: -15px;
        margin-right: -40px;
        border-radius: 50%;
        flex-shrink: 0;
        padding: 0;
        margin-top: 0;
        border: 1px solid ${colors.border};
        background: ${colors.greyBackground};

        span {
            display: none;
        }

        &:hover {
            background: ${colors.greyBackgroundFocused};
        }
    `)}
`